import { ReactNode } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Button } from "../ui/button";

type IconVariant = "success" | "warning" | "info" | "neutral";

interface DialogAction {
  label: string;
  onClick: () => void;
}

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  icon?: ReactNode;
  iconVariant: IconVariant;
  title: string;
  description: string;
  primaryAction?: DialogAction;
  secondaryAction?: DialogAction;
  hideClose?: boolean;
  className?: string;
  primaryButtonClassName?: string;
  secondaryButtonClassName?: string;
}

const iconVariantStyles: Record<IconVariant, string> = {
  success: "bg-[var(--bg-fill-positive)]",
  warning: "bg-[#E0B244]",
  info: "bg-[var(--primary-500,rgba(255,128,19,0.11))]",
  neutral: "bg-black/[0.045]",
};

export function ConfirmDialog({
  open,
  onOpenChange,
  icon,
  iconVariant,
  title,
  description,
  primaryAction,
  secondaryAction,
  hideClose,
  className,
  primaryButtonClassName,
  secondaryButtonClassName,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose={hideClose}
        className={cn(
          "w-[350px] lg:w-[500px] !rounded-none border-black/[0.09] p-6 gap-4 shadow-[0px_8px_16px_0px_rgba(0,0,0,0.09),0px_0px_36px_0px_rgba(0,0,0,0.11)]",
          className,
        )}
      >
        {icon && (
          <div
            className={cn("flex items-center justify-center w-12 h-12 rounded-[4px] bg-[#EBEBEB]")}
          >
            {icon}
          </div>
        )}

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <DialogTitle className="text-2xl font-semibold leading-8 tracking-[-0.02em] text-black">
              {title}
            </DialogTitle>
            <DialogDescription className="text-sm leading-5 tracking-[-0.01em] text-black">
              {description}
            </DialogDescription>
          </div>

          <div className="flex gap-3">
            {secondaryAction && (
              <Button
                variant="default"
                onClick={secondaryAction.onClick}
                className={cn(
                  "flex-1 h-12 min-w-[145px] lg:min-w-[160px] rounded-[4px] bg-[#EBEBEB] text-base font-bold text-black backdrop-blur-[20px]  transition-colors",
                  secondaryButtonClassName,
                )}
              >
                {secondaryAction.label}
              </Button>
            )}
            {primaryAction && (
              <Button
                variant="default"
                onClick={primaryAction.onClick}
                className={cn(
                  "flex-1 h-12 min-w-[145px] lg:min-w-[160px] rounded-[4px] bg-[#FFAA72] text-base font-bold text-black transition-colors",
                  primaryButtonClassName,
                )}
              >
                {primaryAction.label}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
